/**
 * FileList Component
 *
 * Displays railML files in DIM UI's DataTable with:
 * - File metadata (subnetwork, shortname, created date, version, filesize)
 * - Download, copy-link and lineage action buttons
 * - Multi-select checkboxes for bulk zip download
 * - Client-side filtering (title, version, date range, newest-only)
 * - Column sorting, and columns that don't fit moved into expandable rows
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Callout, Select, Switch, Tag, Text } from '@bn-apps/design-system-ui-react';
import { Button, DataTable, SelectionBar, type DataTableColumn } from '@bn-apps/dim-ui-react';
import { Accordion } from '@components/common/Accordion';
import { Download, ContentCopy, Schedule } from '@bn-apps/design-system-icons';
import styles from './FileList.module.scss';
import { getIntlLocale } from '@utils/locale.utils';
import { downloadFile, downloadFilesAsZip, formatFileSize } from '@services/files.service';
import { getRailMLFileLineage } from '@services/sparql.service';
import DataLineageModal from './DataLineageModal';
import type { RailMLFile, DataLineage } from '@/types';
import { Combobox } from '@components/common/Combobox';
import type { ComboboxOption } from '@components/common/Combobox';
import { FilterChip, FilterChipGroup } from '@components/common/FilterChip';
import { useMediaQuery } from '@/hooks/useMediaQuery';

interface FileListProps {
  files: RailMLFile[] | null;
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
}

function getNewestPerGroup(items: RailMLFile[]): Map<string, RailMLFile> {
  const map = new Map<string, RailMLFile>();
  for (const f of items) {
    const key = `${f.title}::${f.schemaVersion}`;
    const existing = map.get(key);
    if (!existing || new Date(f.created).getTime() > new Date(existing.created).getTime()) {
      map.set(key, f);
    }
  }
  return map;
}

function getFilename(file: RailMLFile): string {
  return file.contentUrl.split('/').pop() || file.title;
}

function FileList({ files, loading, error, onRefresh }: FileListProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const isPhone = useMediaQuery('(max-width: 480px)');
  const [selectedFileUris, setSelectedFileUris] = useState<Set<string>>(new Set());
  const [downloadingZip, setDownloadingZip] = useState<boolean>(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [lineageModalOpen, setLineageModalOpen] = useState<boolean>(false);
  const [currentLineage, setCurrentLineage] = useState<DataLineage | null>(null);
  const [lineageLoading, setLineageLoading] = useState<boolean>(false);
  const [currentFileName, setCurrentFileName] = useState<string>('');
  const [filterTitles, setFilterTitles] = useState<ComboboxOption[]>([]);
  const [filterVersions, setFilterVersions] = useState<string[]>([]);
  const [filterDateRange, setFilterDateRange] = useState<string>('');
  const [newestOnly, setNewestOnly] = useState<boolean>(false);

  const locale = getIntlLocale(i18n.language);

  // Derive unique sorted title/description options for filter combobox
  const titleOptions: ComboboxOption[] = useMemo(() => {
    if (!files) return [];
    const titleDescMap = new Map<string, string>();
    for (const f of files) {
      if (!titleDescMap.has(f.title)) {
        titleDescMap.set(f.title, f.description);
      }
    }
    return [...titleDescMap.entries()]
      .sort(([, a], [, b]) => a.localeCompare(b))
      .map(([title, desc]) => ({ value: title, label: `${desc} (${title})` }));
  }, [files]);

  // Compute set of URIs for the latest file per shortname+version group
  const latestFileUris = useMemo((): Set<string> => {
    if (!files) return new Set();
    return new Set([...getNewestPerGroup(files).values()].map((f) => f.uri));
  }, [files]);

  // Client-side filtering
  const filteredData = useMemo((): RailMLFile[] => {
    if (!files) return [];
    let filtered = [...files];

    if (filterTitles.length > 0) {
      const selectedTitleValues = filterTitles.map((opt) => opt.value);
      filtered = filtered.filter((f) => selectedTitleValues.includes(f.title));
    }
    if (filterVersions.length === 0) {
      return [];
    }
    filtered = filtered.filter((f) => filterVersions.includes(f.schemaVersion));
    if (filterDateRange) {
      const now = new Date();
      let cutoff: Date;
      switch (filterDateRange) {
        case 'lastDay':
          cutoff = new Date(now.getTime() - 24 * 60 * 60 * 1000);
          break;
        case 'lastWeek':
          cutoff = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case 'last30Days':
          cutoff = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        default:
          cutoff = new Date(0);
      }
      filtered = filtered.filter((f) => new Date(f.created).getTime() >= cutoff.getTime());
    }
    if (newestOnly) {
      const newestUris = new Set([...getNewestPerGroup(filtered).values()].map((f) => f.uri));
      filtered = filtered.filter((f) => newestUris.has(f.uri));
    }
    return filtered;
  }, [files, filterTitles, filterVersions, filterDateRange, newestOnly]);

  // Derive unique schema versions for filter chips
  const availableVersions = useMemo((): string[] => {
    if (!files) return [];
    return [...new Set(files.map((f) => f.schemaVersion))].sort();
  }, [files]);

  // Initialize filter chips to all selected when versions become available
  useEffect(() => {
    if (availableVersions.length > 0 && filterVersions.length === 0) {
      setFilterVersions(availableVersions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableVersions]);

  // Only count and download the selected rows that the filters show
  const visibleSelected = useMemo(
    () => filteredData.filter((f) => selectedFileUris.has(f.uri)),
    [filteredData, selectedFileUris]
  );

  const isLoading = !files || loading;

  const handleDownloadSelected = useCallback(async (): Promise<void> => {
    if (!visibleSelected.length) return;
    setDownloadingZip(true);
    try {
      setDownloadError(null);
      await downloadFilesAsZip(visibleSelected, 'railml-files.zip');
    } catch (err) {
      console.error('[FileList] Zip download failed:', err);
      setDownloadError(err instanceof Error ? err.message : t('files.error.downloadFailed'));
    } finally {
      setDownloadingZip(false);
    }
  }, [visibleSelected, t]);

  // Action handlers
  const handleDownload = useCallback(async (fileData: RailMLFile): Promise<void> => {
    try {
      await downloadFile(fileData.contentUrl, getFilename(fileData));
    } catch (err) {
      console.error('[FileList] Download failed:', err);
      setDownloadError(err instanceof Error ? err.message : t('files.error.downloadFailed'));
    }
  }, [t]);

  const handleCopyLink = useCallback(async (fileData: RailMLFile): Promise<void> => {
    try {
      const fullUrl = new URL(`/download${fileData.contentUrl}`, window.location.origin).href;
      await navigator.clipboard.writeText(fullUrl);
    } catch (err) {
      console.error('[FileList] Copy link failed:', err);
    }
  }, []);

  const handleLineageClick = useCallback(async (fileData: RailMLFile): Promise<void> => {
    setCurrentFileName(fileData.title);
    setLineageModalOpen(true);
    setLineageLoading(true);
    setCurrentLineage(null);
    try {
      const lineage = await getRailMLFileLineage(fileData.uri);
      setCurrentLineage(lineage);
    } catch (err) {
      console.error('[FileList] Failed to fetch lineage:', err);
    } finally {
      setLineageLoading(false);
    }
  }, []);

  const formatDateTime = useCallback((dateValue: Date | string): string =>
    new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(dateValue)),
  [locale]);

  const columns = useMemo<DataTableColumn<RailMLFile>[]>(() => {
    const downloadButton = (file: RailMLFile) => (
      <button
        type="button"
        className={styles.iconButton}
        aria-label={t('files.download.aria', { filename: getFilename(file) })}
        title={t('files.download.button')}
        onClick={() => void handleDownload(file)}
      >
        <Download />
      </button>
    );
    const moreButtons = (file: RailMLFile) => (
      <>
        <button
          type="button"
          className={styles.iconButton}
          aria-label={t('files.download.copyLinkAria', { filename: getFilename(file) })}
          title={t('files.download.copyLink')}
          onClick={() => void handleCopyLink(file)}
        >
          <ContentCopy />
        </button>
        <button
          type="button"
          className={styles.iconButton}
          aria-label={t('files.dataLineage.buttonAria', { filename: getFilename(file) })}
          title={t('files.dataLineage.button')}
          onClick={() => void handleLineageClick(file)}
        >
          <Schedule />
        </button>
      </>
    );

    const dataColumns: DataTableColumn<RailMLFile>[] = [
      {
        id: 'description',
        header: t('files.table.description'),
        minWidth: 160,
        sortValue: (file) => file.description,
        cell: (file) => (
          <span className={styles.descriptionCell}>
            {file.description}
            {latestFileUris.has(file.uri) && <Tag text={t('files.tag.latest')} size="sm" color="green" />}
          </span>
        ),
      },
      {
        id: 'title',
        header: t('files.table.shortname'),
        minWidth: 100,
        priority: 3,
        sortValue: (file) => file.title,
        cell: (file) => file.title,
      },
      {
        id: 'created',
        header: t('files.table.created'),
        minWidth: 150,
        priority: 2,
        sortValue: (file) => new Date(file.created),
        cell: (file) => formatDateTime(file.created),
      },
      {
        id: 'schemaVersion',
        header: t('files.table.version'),
        minWidth: 80,
        priority: 4,
        sortValue: (file) => file.schemaVersion,
        cell: (file) => file.schemaVersion,
      },
      {
        id: 'filesize',
        header: t('files.table.filesize'),
        minWidth: 100,
        priority: 1,
        numeric: true,
        sortValue: (file) => Number(file.filesize),
        cell: (file) => formatFileSize(file.filesize),
      },
    ];

    // On phones the three 44px buttons don't fit next to long subnetwork names,
    // so all but Download move into the row's details.
    if (isPhone) {
      return [
        ...dataColumns,
        {
          id: 'actions',
          header: t('files.table.actions'),
          hideHeader: true,
          minWidth: 68,
          cell: (file) => <div className={styles.actionButtons}>{downloadButton(file)}</div>,
        },
        {
          id: 'moreActions',
          header: t('files.table.moreActions'),
          priority: 5,
          cell: (file) => <div className={styles.actionButtons}>{moreButtons(file)}</div>,
        },
      ];
    }
    return [
      ...dataColumns,
      {
        id: 'actions',
        header: t('files.table.actions'),
        hideHeader: true,
        minWidth: 150,
        cell: (file) => (
          <div className={styles.actionButtons}>
            {downloadButton(file)}
            {moreButtons(file)}
          </div>
        ),
      },
    ];
  }, [t, isPhone, latestFileUris, formatDateTime, handleDownload, handleCopyLink, handleLineageClick]);

  const selectionBar = (
    <SelectionBar
      count={isLoading ? 0 : visibleSelected.length}
      label={(count) => t('files.selection.selectedCount', { count })}
      clearLabel={t('common.clear')}
      onClear={() => setSelectedFileUris(new Set())}
    >
      <Button
        className={styles.zipButton}
        hierarchy="primary"
        size="sm"
        iconStart={Download}
        isBusy={downloadingZip}
        onClick={handleDownloadSelected}
      >
        {t('files.selection.downloadZip')}
      </Button>
    </SelectionBar>
  );

  const filterControls = (
    <>
      <div className={styles.filterSubnetwork}>
        <Combobox
          id="filter-title"
          label={t('files.table.description')}
          options={titleOptions}
          selectedValues={filterTitles}
          onSelectionChange={setFilterTitles}
          placeholder={t('combobox.placeholder')}
        />
      </div>
      <div className={styles.filterVersion}>
        <label className={styles.filterLabel}>
          {t('files.table.version')}
        </label>
        <FilterChipGroup aria-label={t('files.filters.version-aria')}>
          {availableVersions.map((version) => (
            <FilterChip
              key={version}
              size="sm"
              isSelected={filterVersions.includes(version)}
              onClick={() =>
                setFilterVersions((prev) =>
                  prev.includes(version)
                    ? prev.filter((v) => v !== version)
                    : [...prev, version]
                )
              }
            >
              {t('files.filters.railMLversion', { version })}
            </FilterChip>
          ))}
        </FilterChipGroup>
      </div>
      <div className={styles.filterCreated}>
        <Select
          label={t('files.filters.dateRange')}
          selectSize="md"
          value={filterDateRange}
          onChange={(e) => setFilterDateRange(e.target.value)}
        >
          <option value="">{t('common.all')}</option>
          <option value="lastDay">{t('files.filters.lastDay')}</option>
          <option value="lastWeek">{t('files.filters.lastWeek')}</option>
          <option value="last30Days">{t('files.filters.last30Days')}</option>
        </Select>
      </div>
      <div className={styles.filterLatest}>
        <Switch
          label={t('files.tag.latest')}
          checked={newestOnly}
          onChange={(e) => setNewestOnly(e.target.checked)}
        />
      </div>
    </>
  );

  return (
    <div className="bn-mbe-1600">

      {downloadError && (
        <Callout
          calloutStatus="critical"
          title={t('files.error.downloadFailed')}
          onDismiss={() => setDownloadError(null)}
        >
          {downloadError}
        </Callout>
      )}

      {/* Filter bar - accordion on mobile, grid on desktop */}
      {isMobile ? (
        <>
          <div className={styles.filterAccordionWrapper}>
            <Accordion heading={t('files.filters.title')}>
              <div className={styles.filterBarMobile}>
                {filterControls}
              </div>
            </Accordion>
          </div>
          {visibleSelected.length > 0 && selectionBar}
        </>
      ) : (
        <div className={styles.filterBar}>
          {filterControls}
          <div className={styles.filterZip}>{selectionBar}</div>
        </div>
      )}

      <div className={styles.rowCountBar}>
        <Text fontSize="sm" style={{ visibility: isLoading ? 'hidden' : 'visible' }}>
          {t('files.count', { count: filteredData.length })}
        </Text>
      </div>

      <DataTable
        label={t('files.table.aria.label')}
        columns={columns}
        rows={filteredData}
        rowKey={(file) => file.uri}
        rowLabel={getFilename}
        defaultSort={{ column: 'created', direction: 'descending' }}
        selectionMode="multiple"
        selectedKeys={selectedFileUris}
        onSelectionChange={setSelectedFileUris}
        selectOnRowClick
        maxHeight="600px"
        isLoading={isLoading}
        empty={files?.length ? t('files.empty.withFilter') : t('files.empty.noFiles')}
        error={error && t('files.error.loadingFiles')}
        onRetry={onRefresh}
        labels={{
          selectAll: t('files.selection.selectAll'),
          selectRow: (filename) => t('files.selection.selectRow', { filename }),
          details: t('files.table.details'),
          showDetails: (filename) => t('files.table.expandRow', { filename }),
          retry: t('common.retry'),
        }}
      />

      <DataLineageModal
        isOpen={lineageModalOpen}
        onClose={() => setLineageModalOpen(false)}
        lineage={currentLineage}
        loading={lineageLoading}
        fileName={currentFileName}
      />
    </div>
  );
}

export default FileList;
